import { forwardRef, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useAutoResizeTextarea } from '../useAutoResizeTextarea'
import Styles from './V4ReviewTopics.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Image03, Images, VideoOn, Email, ClockCheck } from 'blaze-ui/icons/24'
import { File02 } from 'blaze-ui/icons/20'
import { Iphone02 } from 'blaze-ui/icons/16'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { updateBrandPlanScheduleContent } from 'hooks/queries/schedules/useUpdateBrandPlanScheduleContent'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BrandPlanScheduleContent, BrandPlanTopics } from 'types'
import { ContentCategory } from 'utils/brandPlans/contentCategories'
import { useUpdateReferenceImage } from 'hooks/queries/schedules/contents/useUpdateReferenceImage'
import { useCreateReferenceImage } from 'hooks/queries/schedules/contents/useCreateReferenceImage'
import { useReferenceImage } from 'hooks/queries/schedules/contents/useReferenceImage'
import { useIsMutating } from 'hooks/useIsMutating'
import { CONTENT_CATEGORY_THUMBNAILS } from 'utils/brandPlans/contentCategories'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ButtonProps } from '@react-types/button'
import { useButton } from '@react-aria/button'
import { filterDOMProps, useObjectRef } from '@react-aria/utils'
import { AriaLabelingProps } from '@react-types/shared'
import cx from 'classnames'
import { LazyCreateReferenceImage } from 'screens/VirtualMarketerOnboarding/V4ReviewTopics/LazyCreateReferenceImage'
import { ReferenceImageTrigger as BaseReferenceImageTrigger } from 'screens/Schedule/PostPromptsSection/ReferenceImageTrigger'

const referenceImageUpdateMutationKey = (scheduleId: number) => ['referenceImageUpdate', scheduleId] as const

const CONTENT_TYPE_TO_TOPIC_FIELD: Record<string, keyof BrandPlanTopics> = {
  instagram_post: 'instagram_topics',
  instagram_reel: 'video_crosspost_topics',
  instagram_story: 'instagram_topics',
  facebook_post: 'facebook_topics',
  facebook_story: 'facebook_topics',
  linkedin_post: 'linkedin_topics',
  twitter_post: 'twitter_topics',
  twitter_thread: 'twitter_topics',
  tiktok_post: 'tiktok_topics',
  youtube_short: 'youtube_topics',
  crosspost_post: 'crosspost_topics',
  video_crosspost_post: 'video_crosspost_topics',
  newsletter: 'email_topics',
  blog_post: 'seo_topics',
  google_my_business_post: 'google_my_business_topics',
}

const CONTENT_TYPE_TO_CATEGORY: Record<string, ContentCategory> = {
  crosspost_post: 'still_image_post',
  video_crosspost_post: 'short_form_video',
  instagram_post: 'still_image_post',
  facebook_post: 'still_image_post',
  linkedin_post: 'still_image_post',
  twitter_post: 'still_image_post',
  google_my_business_post: 'still_image_post',
  instagram_reel: 'short_form_video',
  tiktok_post: 'short_form_video',
  youtube_short: 'short_form_video',
  instagram_story: 'stories',
  facebook_story: 'stories',
  blog_post: 'blog_post',
  newsletter: 'newsletter',
}

const CROSSPOST_CONTENT_TYPE_TO_CATEGORY: Record<string, ContentCategory> = {
  still_image: 'still_image_post',
  carousel: 'carousel_post',
  video: 'feed_video_post',
}

function categoryForContent(content: {
  type: string
  content_type?: string | null
  is_crosspost?: boolean
}): ContentCategory {
  if (content.is_crosspost && content.content_type) {
    return CROSSPOST_CONTENT_TYPE_TO_CATEGORY[content.content_type] ?? 'still_image_post'
  }
  return CONTENT_TYPE_TO_CATEGORY[content.type] ?? 'still_image_post'
}

const CATEGORY_LABELS: Record<ContentCategory, string> = {
  still_image_post: 'Still Image',
  carousel_post: 'Carousel Post',
  feed_video_post: 'Feed Video Post',
  short_form_video: 'Short Form Video',
  stories: 'Story',
  newsletter: 'Email',
  blog_post: 'Blog Post',
}

type IconFC = React.FC<{ size?: number; color?: string }>

export const CATEGORY_ICONS: Record<ContentCategory, { icon: IconFC; color: string }> = {
  still_image_post: { icon: Image03, color: '#FD4242' },
  carousel_post: { icon: Images, color: '#EF6800' },
  feed_video_post: { icon: VideoOn, color: '#6A00FF' },
  short_form_video: { icon: Iphone02 as IconFC, color: '#00AAFF' },
  stories: { icon: ClockCheck as IconFC, color: '#FF37CA' },
  newsletter: { icon: Email, color: '#FFAE00' },
  blog_post: { icon: File02 as IconFC, color: '#20A14F' },
}
const SOCIAL_CROSSPOST_TYPES = new Set([
  'instagram_post',
  'instagram_story',
  'facebook_post',
  'facebook_story',
  'linkedin_post',
  'twitter_post',
])

const VIDEO_CROSSPOST_TYPES = new Set(['instagram_reel', 'tiktok_post', 'youtube_short'])

function buildScheduleTopicArrays(
  contents: BrandPlanScheduleContent[],
  editedTopics: Record<number, string>,
): Partial<BrandPlanTopics> {
  const topicArrays: Record<string, string[]> = {}

  for (const content of contents) {
    let field: keyof BrandPlanTopics | undefined

    if (content.is_crosspost) {
      if (SOCIAL_CROSSPOST_TYPES.has(content.type)) {
        field = 'crosspost_topics'
      } else if (VIDEO_CROSSPOST_TYPES.has(content.type)) {
        field = 'video_crosspost_topics'
      } else {
        field = CONTENT_TYPE_TO_TOPIC_FIELD[content.type]
      }
    } else {
      field = CONTENT_TYPE_TO_TOPIC_FIELD[content.type]
    }

    if (!field) continue
    const topic = editedTopics[content.id] ?? content.topic
    if (!topic) continue
    if (!topicArrays[field]) topicArrays[field] = []
    topicArrays[field].push(topic)
  }

  return topicArrays as Partial<BrandPlanTopics>
}

const TriggerButton = forwardRef<HTMLButtonElement, ButtonProps & AriaLabelingProps>(({ children, ...props }, fRef) => {
  const ref = useObjectRef(fRef)
  const { buttonProps } = useButton(props, ref)
  return (
    <button ref={ref} {...buttonProps} {...filterDOMProps(props, { labelable: true })} className={Styles.triggerButton}>
      {children}
    </button>
  )
})

interface ReferenceImageTriggerProps {
  scheduleId: number
  contentId: number
  category: ContentCategory
  label: string
  onImageSet: () => void
}

const ReferenceImageTrigger = ({ scheduleId, contentId, category, label, onImageSet }: ReferenceImageTriggerProps) => {
  const { data: referenceImage } = useReferenceImage({ scheduleId, contentId })
  const { mutate: updateReferenceImage, isLoading: isUpdating } = useUpdateReferenceImage(
    { scheduleId, contentId },
    {
      mutationKey: referenceImageUpdateMutationKey(scheduleId),
      onSuccess: () => onImageSet(),
      onError: () => {
        showToastMessage('Could not update the reference image. Please try again.', 'error')
      },
    },
  )

  const mediaFile = referenceImage?.media_file
  const isFallback = !mediaFile
  const isDisabled = isUpdating

  return (
    <BaseReferenceImageTrigger
      onUse={(mediaFile) => {
        updateReferenceImage({
          media_file: mediaFile,
        })
      }}
    >
      <TriggerButton aria-label="Change Reference Image" isDisabled={isDisabled}>
        <div className={cx(Styles.thumbnail, { [Styles.thumbnailInteractive]: !isDisabled })}>
          {isFallback ? (
            <>
              <img src={CONTENT_CATEGORY_THUMBNAILS[category]} alt={label} />
              <div className={Styles.thumbnailOverlay}>
                <span className={Styles.referenceLabel}>Reference</span>
              </div>
            </>
          ) : (
            <LazyCreateReferenceImage
              scheduleId={scheduleId}
              contentId={contentId}
              alt={label}
              width={124}
              height={124}
            />
          )}
        </div>
      </TriggerButton>
    </BaseReferenceImageTrigger>
  )
}

interface TopicRowProps {
  scheduleId: number
  contentId: number
  category: ContentCategory
  label: string
  icon: IconFC
  iconColor: string
  topic: string
  onTopicChange: (value: string) => void
  onImageSet: () => void
}

const TopicRow = ({
  scheduleId,
  contentId,
  category,
  label,
  icon: Icon,
  iconColor,
  topic,
  onTopicChange,
  onImageSet,
}: TopicRowProps) => {
  const { ref, resize } = useAutoResizeTextarea(topic)

  return (
    <div className={Styles.topicRow}>
      <ReferenceImageTrigger
        scheduleId={scheduleId}
        contentId={contentId}
        category={category}
        label={label}
        onImageSet={onImageSet}
      />

      <div className={Styles.topicContent}>
        <div className={Styles.contentTypeRow}>
          <Icon size={20} color={iconColor} />
          <Text color="var(--dark-90)">{label}</Text>
        </div>

        <div className={Styles.topicField}>
          <Text variant="metadata" color="var(--dark-60)">
            Topic:
          </Text>
          <textarea
            ref={ref}
            className={Styles.topicTextarea}
            value={topic}
            onChange={(e) => {
              onTopicChange(e.target.value)
              resize()
            }}
            placeholder="Topic for this post"
          />
        </div>
      </div>
    </div>
  )
}

interface Props {
  scheduleId: number
  onNext: () => void
  onBack: () => void
}

export const V4ReviewTopics = ({ scheduleId, onNext, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const queryClient = useQueryClient()
  const { data: schedule, isLoading } = useBrandPlanSchedule(scheduleId)
  const { mutateAsync: updateSchedule } = useUpdateBrandPlanSchedule({ id: scheduleId })
  const { mutate: createReferenceImage } = useCreateReferenceImage()

  const [editedTopics, setEditedTopics] = useState<Record<number, string>>({})
  const [userSetImageIds, setUserSetImageIds] = useState<Set<number>>(new Set())
  const { isSaving, run } = useStepAction()

  const isPatchingReferenceImage = useIsMutating({ mutationKey: referenceImageUpdateMutationKey(scheduleId) }) > 0

  const handleTopicChange = (contentId: number, value: string) => {
    setEditedTopics((prev) => ({ ...prev, [contentId]: value }))
  }

  const handleImageSet = (contentId: number) => {
    setUserSetImageIds((prev) => new Set([...prev, contentId]))
  }

  const handleNext = () => {
    const changedEntries = Object.entries(editedTopics)
    if (changedEntries.length === 0) {
      onNext()
      return
    }

    run(async () => {
      const topicArrays = buildScheduleTopicArrays(schedule?.contents ?? [], editedTopics)
      const contentUpdates = changedEntries.map(([contentId, topic]) =>
        updateBrandPlanScheduleContent(scheduleId, Number(contentId), { topic }),
      )
      await Promise.all([updateSchedule(topicArrays), ...contentUpdates])
      queryClient.invalidateQueries(scheduleKeys.detail(scheduleId))

      changedEntries.forEach(([contentId]) => {
        const id = Number(contentId)
        if (!userSetImageIds.has(id)) {
          createReferenceImage({ scheduleId, contentId: id })
        }
      })

      onNext()
    }, 'Something went wrong saving your topics. Please try again.')
  }

  const CATEGORY_ORDER: Record<ContentCategory, number> = {
    still_image_post: 0,
    carousel_post: 1,
    feed_video_post: 2,
    short_form_video: 3,
    stories: 4,
    newsletter: 5,
    blog_post: 6,
  }

  // Per-content index within its category, computed once so the render is side-effect-free.
  // Safe to call unconditionally before the early return below.
  const { sortedContents, contentCategoryIndex } = useMemo(() => {
    const sorted = [...(schedule?.contents ?? [])].sort(
      (a, b) => CATEGORY_ORDER[categoryForContent(a)] - CATEGORY_ORDER[categoryForContent(b)],
    )
    const perContent: Record<number, number> = {}
    const running: Record<string, number> = {}
    for (const content of sorted) {
      const category = categoryForContent(content)
      running[category] = (running[category] ?? 0) + 1
      perContent[content.id] = running[category]
    }
    return { sortedContents: sorted, contentCategoryIndex: perContent }
  }, [schedule?.contents]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !schedule) {
    return null
  }

  return (
    <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={handleNext} isSaving={isSaving} isDisabled={isPatchingReferenceImage}>
        <div className={Styles.content}>
          <TopBar highlightStep="campaign" />

          <div className={Styles.headerSection}>
            <Heading level={isMobile ? 2 : 1}>Review your topics and their reference images.</Heading>
            <Text color="var(--dark-60)">
              You can think of these as what directs the main subject and target of the content Blaze generates.
            </Text>
          </div>

          <div className={Styles.topicList}>
            {sortedContents.map((content) => {
              const category = categoryForContent(content)
              const count = contentCategoryIndex[content.id]
              const label = `${CATEGORY_LABELS[category]} ${count}`
              const { icon: Icon, color: iconColor } = CATEGORY_ICONS[category]

              return (
                <TopicRow
                  key={content.id}
                  scheduleId={scheduleId}
                  contentId={content.id}
                  category={category}
                  label={label}
                  icon={Icon}
                  iconColor={iconColor}
                  topic={editedTopics[content.id] ?? content.topic ?? ''}
                  onTopicChange={(value) => handleTopicChange(content.id, value)}
                  onImageSet={() => handleImageSet(content.id)}
                />
              )
            })}
          </div>
        </div>
      </V4StepFooter>
    </MultiStepStepLayout>
  )
}
